<template>
  <section :class="$style.page">
    <img
      src="@/assets/images/logo.svg?url"
      :class="[$style.page__logo, $style.logo]"
    />

    <template v-if="isTokenInvalid">
      <header :class="[$style.page__header, $style.header]">
        <h2 :class="$style.header__title">エラー</h2>
      </header>
      <section :class="[$style.page__action, $style.action]">
        <p :class="$style.action__text" v-if="isTokenLengthInvalid">
          指定されたURLが正しくありません。 <br />
          メールに記載されたURLをご確認の再度アクセスしてください。
        </p>
        <p :class="$style.action__text" v-else>
          再設定URLが正しくありません。<br />
          URLが誤っているか、有効期限が切れています。<br />
          お手数をおかけしますが、<RouterLink
            :to="{ name: 'resetPassword' }"
            :class="$style.textlink"
            >こちら</RouterLink
          >をクリックして最初からやり直して下さい。
        </p>
      </section>
    </template>

    <template v-else>
      <template v-if="!isCompleted">
        <header :class="[$style.page__header, $style.header]">
          <h2 :class="$style.header__title">パスワードの再設定</h2>
          <p :class="$style.header__body">
            新しく設定するパスワードを入力してください。
          </p>
        </header>
        <section :class="$style.page__main">
          <RequestForm
            @submit="onSubmit"
            v-model="password"
            sendButtonText="パスワードを再設定"
            inputType="password"
            inputName="パスワード"
            :submitDisabled="isLoading"
          />
        </section>
      </template>

      <template v-else>
        <header :class="[$style.page__header, $style.header]">
          <h2 :class="$style.header__title">パスワードの再設定</h2>
          <p :class="$style.header__body">パスワードを再設定しました。</p>
        </header>
        <div :class="[$style.page__action, $style.action]">
          <div :class="$style.action__buttons">
            <FlatRouterLinkButton
              :to="{ name: 'login' }"
              variant="primary"
              size="medium"
            >
              ログインページへ
            </FlatRouterLinkButton>
          </div>
        </div>
      </template>
    </template>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import RequestForm from './RequestForm.vue'
import { getQuery } from '@/libs/utilities/getQuery'
import FlatRouterLinkButton from '@/components/atoms/FlatRouterLinkButton.vue'
import { isClientError } from '@/libs/utilities/CustomErrors'
import { captureException } from '@sentry/vue'
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import { useAlert } from '@/libs/alertPortal'
import {
  useVerifyPasswordResetTokenMutation,
  useChangePasswordWithoutLoggedInMutation,
} from './PasswordResetChangePage.generated'

useHead({
  title: 'パスワード設定',
})

const password = ref('')
const isCompleted = ref(false)
const isLoading = ref(false)
const isTokenInvalid = ref(false)

const route = useRoute('resetPasswordVerify')

const token = computed<Maybe<string>>(() => getQuery(route.params.token))
const isTokenLengthInvalid = computed<boolean>(() => token.value?.length !== 36)

const verifyPasswordResetTokenMutation = useVerifyPasswordResetTokenMutation()
const changePasswordWithoutLoggedInMutation =
  useChangePasswordWithoutLoggedInMutation()

onMounted(async () => {
  if (token.value === undefined || isTokenLengthInvalid.value) {
    isTokenInvalid.value = true
    return
  }
  try {
    const result = await verifyPasswordResetTokenMutation.mutate({
      token: token.value,
    })
    if (result?.data == null) {
      isTokenInvalid.value = true
      return
    }

    isTokenInvalid.value = !result.data.verifyPasswordResetToken.verified
  } catch (error) {
    console.error(error)
    isTokenInvalid.value = true
  }
})

const alert = useAlert()

const onSubmit = async (): Promise<void> => {
  if (token.value === undefined) {
    return
  }
  try {
    isLoading.value = true
    await changePasswordWithoutLoggedInMutation.mutate({
      password: password.value,
      token: token.value,
    })
    isCompleted.value = true
  } catch (error) {
    console.error(error)
    alert.error('申し訳ございませんが、エラーです。')

    if (!isClientError(error)) {
      captureException(error)
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" module>
.page {
  &__header {
    &:nth-child(n + 2) {
      margin-top: 48px;
    }
  }

  &__main {
    width: 480px;
    padding: 40px;
    margin-right: auto;
    margin-left: auto;
    background-color: $color-surface-secondary;
    border-radius: $border-radius-v2-base;

    &:nth-child(n + 2) {
      margin-top: 48px;
    }

    &--nobg {
      background-color: initial;
    }
  }

  &__action {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.logo {
  display: block;
  width: 320px;
  margin-right: auto;
  margin-left: auto;
}

.header {
  text-align: center;

  &__title {
    font-size: 24px;
    font-weight: bold;
  }

  &__body {
    font-size: 14px;
    line-height: 24px;

    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.action {
  &__text {
    font-size: 14px;
    line-height: 24px;
    text-align: center;
  }

  &__buttons {
    display: flex;
    place-content: center center;

    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.textlink {
  color: $color-text-and-object-accent-primary;
}
</style>
